import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Profile, LoginCredentials } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (profileData: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const profileData = await apiService.getProfile();
          
          // Gestione dei due formati possibili dell'API usando type guards
          if ('user' in profileData && 'profile' in profileData) {
            // Formato endpoint accounts - ha user e profile separati
            const combinedProfile = {
              ...profileData.profile,
              user: profileData.user,
              codice_fiscale: profileData.profile.codice_fiscale || '',
              mobile_number: profileData.profile.mobile_number || '',
              date: profileData.profile.date || new Date().toISOString(),
              date_update: profileData.profile.date_update || new Date().toISOString(),
              is_active: profileData.profile.is_active ?? true
            };
            setProfile(combinedProfile);
            setUser(profileData.user);
          } else if ('id' in profileData && 'first_name' in profileData) {
            // Formato endpoint fattura - solo profilo diretto
            const mockUser = {
              id: profileData.id,
              username: profileData.email || 'user', // Fallback se non abbiamo username
              email: profileData.email,
              first_name: profileData.first_name,
              last_name: profileData.last_name
            };
            
            const combinedProfile = {
              ...profileData,
              user: mockUser,
              codice_fiscale: profileData.codice_fiscale || '',
              mobile_number: profileData.mobile_number || '',
              date: profileData.date || new Date().toISOString(),
              date_update: profileData.date_update || new Date().toISOString(),
              is_active: profileData.is_active ?? true
            };
            
            setProfile(combinedProfile);
            setUser(mockUser);
          }
        } catch (error) {
          console.error('Errore nel caricamento del profilo:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    console.log('🔐 [AUTH] Inizio processo di login');
    console.log('🔐 [AUTH] Credenziali ricevute:', credentials);
    
    try {
      console.log('🔐 [AUTH] Chiamata apiService.login()');
      const response = await apiService.login(credentials);
      console.log('✅ [AUTH] Login API riuscito, risposta:', response);
      
      console.log('💾 [AUTH] Salvataggio token nel localStorage');
      console.log('💾 [AUTH] Access token da salvare:', response.access_token);
      console.log('💾 [AUTH] Refresh token da salvare:', response.refresh_token);
      
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Verifica immediata del salvataggio
      const savedAccess = localStorage.getItem('access_token');
      const savedRefresh = localStorage.getItem('refresh_token');
      console.log('💾 [AUTH] Token salvati - Verifica:');
      console.log('💾 [AUTH] Access token salvato:', savedAccess);
      console.log('💾 [AUTH] Refresh token salvato:', savedRefresh);
      console.log('💾 [AUTH] Token salvati con successo');
      
      console.log('👤 [AUTH] Recupero profilo utente');
      
      // Verifica che il token sia disponibile prima di chiamare getProfile
      const currentToken = localStorage.getItem('access_token');
      console.log('🔑 [AUTH] Token corrente prima di getProfile:', currentToken);
      
      if (!currentToken) {
        throw new Error('Token non disponibile dopo il login');
      }
      
      console.log('🚀 [AUTH] Sto per chiamare apiService.getProfile()...');
      
      let profileData;
      try {
        profileData = await apiService.getProfile();
        console.log('🎯 [AUTH] getProfile() completato, dati ricevuti:', profileData);
      } catch (profileError: any) {
        console.error('❌ [AUTH] Errore nella chiamata getProfile():', profileError);
        console.error('❌ [AUTH] Status:', profileError.response?.status);
        console.error('❌ [AUTH] Data:', profileError.response?.data);
        throw new Error(`Errore nel recupero profilo: ${profileError.message}`);
      }
      
      console.log('✅ [AUTH] Profilo recuperato:', profileData);
      console.log('✅ [AUTH] Chiavi del profilo:', Object.keys(profileData || {}));
      
      if (!profileData) {
        throw new Error('Profilo non presente nella risposta');
      }
      
      // L'API Django restituisce direttamente i dati del profilo
      // Ma manca l'oggetto user, dobbiamo crearlo dai dati salvati o dai token
      console.log('🔍 [AUTH] Analisi struttura risposta:', Object.keys(profileData));
      
      // Se la risposta ha già user e profile separati (endpoint accounts)
      if ('user' in profileData && 'profile' in profileData) {
        console.log('✅ [AUTH] Formato endpoint accounts - user e profile separati');
        const combinedProfile = {
          ...profileData.profile,
          user: profileData.user,
          codice_fiscale: profileData.profile.codice_fiscale || '',
          mobile_number: profileData.profile.mobile_number || '',
          date: profileData.profile.date || new Date().toISOString(),
          date_update: profileData.profile.date_update || new Date().toISOString(),
          is_active: profileData.profile.is_active ?? true
        };
        setProfile(combinedProfile);
        setUser(profileData.user);
      } 
      // Se la risposta è solo il profilo (endpoint fattura)
      else if ('id' in profileData && 'first_name' in profileData) {
        console.log('✅ [AUTH] Formato endpoint fattura - solo profilo, creo user mock');
        
        // Creiamo un oggetto user di base per far funzionare l'autenticazione
        const mockUser = {
          id: profileData.id,
          username: credentials.username, // Usiamo lo username dal login
          email: profileData.email,
          first_name: profileData.first_name,
          last_name: profileData.last_name
        };
        
        const combinedProfile = {
          ...profileData,
          user: mockUser,
          codice_fiscale: profileData.codice_fiscale || '',
          mobile_number: profileData.mobile_number || '',
          date: profileData.date || new Date().toISOString(),
          date_update: profileData.date_update || new Date().toISOString(),
          is_active: profileData.is_active ?? true
        };
        
        console.log('🔄 [AUTH] Profilo con user mock:', combinedProfile);
        setProfile(combinedProfile);
        setUser(mockUser);
      } 
      // Fallback - formato non riconosciuto
      else {
        console.error('❌ [AUTH] Struttura risposta non riconosciuta:', profileData);
        throw new Error(`Struttura risposta non valida. Chiavi disponibili: ${Object.keys(profileData).join(', ')}`);
      }
      
      // Aspettiamo un tick per essere sicuri che lo stato sia aggiornato prima di completare
      await new Promise(resolve => setTimeout(resolve, 0));
      console.log('✅ [AUTH] Login completato con successo - stato aggiornato');
    } catch (error:any) {
      console.error('❌ [AUTH] Errore durante il login:', error);
      console.error('❌ [AUTH] Stack trace:', error.stack);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Errore durante il logout:', error);
    } finally {
      setUser(null);
      setProfile(null);
      // Cancella anche la specialistica selezionata dal localStorage
      localStorage.removeItem('selectedSpecialty');
    }
  };

  const updateProfile = async (profileData: Partial<Profile>) => {
    try {
      const updatedProfile = await apiService.updateProfile(profileData);
      setProfile(updatedProfile);
      setUser(updatedProfile.user);
    } catch (error) {
      console.error('Errore durante l\'aggiornamento del profilo:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    profile,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 